import api from './api';

/** One row in the accessorial list modal (deleted rows shown red). */
export interface AccessorialListRow {
  recno: number;
  accessorialNum: number;
  billToName: string;
  billFromSystem: string;
  doNotBillReason: string;
  invoiceDate: string; // display MM/DD/YYYY
  masExportDate: string; // display MM/DD/YYYY; set => "paid out" (Sage-exported)
  invoiceTotal: number;
  deleted: string;
}

export interface AccessorialListResponse {
  count: number;
  records: AccessorialListRow[];
  /** Comma-joined receiving warehouses — feeds the GL-code red-flag in create mode. */
  rcvWhseList: string;
}

/** Full accessorial header for the edit modal. */
export interface AccessorialRecord {
  recno: number;
  accessorialNum: number;
  billTo: number;
  billToName: string;
  billFromSystem: string;
  doNotBillReason: string;
  creditAmount: number;
  creditReason: string;
  invoiceTotal: number;
  invoiceComments: string;
  billToRefNo: string;
  invoiceDate: string; // ISO YYYY-MM-DD
  masExportDate: string; // ISO YYYY-MM-DD
  /** Customer-facing invoice number (invoice_history.masterpro); blank until invoiced. */
  invoiceNumber: string;
  /** The raised invoice itself — what the Invoice Options modal acts on. 0 until
   *  the accessorial is invoiced. */
  invHistoryRecno?: number;
  quoteNumber: number;
  revisionNumber: number;
  v2QuoteNumber: number;
  v2Revision: number;
  v2Lane: string;
  requireRevisedDo: string;
  requirePod: string;
  requireBol: string;
  requireMrpl: string;
  requireInInterchange: string;
  requireOutInterchange: string;
  requireChassisInEir: string;
  requireChassisOutEir: string;
  requireEdi: string;
  invFullEmpty: string;
  pendingApproval: string;
  deleted: string;
  invoiced: boolean;
  createdBy: string;
  createdAtDisplay: string;
  rcvWhseList: string;
  missingInvApp: string; // 'Y' | 'N' (order-level)
  missingInvReason: string; // order-level
}

/** Writable accessorial header payload (create + update). Line items are saved
 *  separately via invoiceLineItemsService.saveBatch(pro, rows, accessorialNum). */
export interface AccessorialPayload {
  billTo: number;
  billFromSystem: string;
  doNotBillReason: string;
  creditAmount: string;
  creditReason: string;
  /** Accounting-owned and NOT writable through this route — invoicedate is stamped by
   *  invoice creation, masexportdate by the Sage export. The server ignores both here;
   *  the fields stay in the type only so an older cached bundle still type-checks. */
  invoiceDate?: string;
  masExportDate?: string;
  billToRefNo: string;
  invoiceComments: string;
  quoteNumber: string;
  revisionNumber: string;
  v2QuoteNumber: string;
  v2Revision: string;
  v2Lane: string;
  requireRevisedDo: string;
  requirePod: string;
  requireBol: string;
  requireMrpl: string;
  requireInInterchange: string;
  requireOutInterchange: string;
  requireChassisInEir: string;
  requireChassisOutEir: string;
  requireEdi: string;
  invFullEmpty: string;
  pendingApproval: string;
  /** Write-ignored on update — `accessorials.py:_DELETE_ONLY_COLS` skips it, so the
   *  only way to set it is `remove()`. Optional because the edit modal no longer
   *  sends one; on CREATE the server defaults it to 'N'. */
  deleted?: string;
  missingInvApp: string;
  missingInvReason: string;
}

export interface AccessorialSaveResult {
  recno: number;
  accessorialNum: number;
}

// pronumber is number|string so a "T…" template pro routes to templateinvoice.
export const accessorialsService = {
  list: (pronumber: number | string): Promise<AccessorialListResponse> =>
    api.get(`/orders/${pronumber}/accessorials`).then((r) => r.data),
  get: (pronumber: number | string, recno: number): Promise<AccessorialRecord> =>
    api.get(`/orders/${pronumber}/accessorials/${recno}`).then((r) => r.data),
  create: (pronumber: number | string, body: AccessorialPayload): Promise<AccessorialSaveResult> =>
    api.post(`/orders/${pronumber}/accessorials`, body).then((r) => r.data),
  update: (
    pronumber: number | string,
    recno: number,
    body: AccessorialPayload,
    /** Admin Override (#33501): edit an accessorial that has already been invoiced.
     *  Without it the backend 409s. Validated server-side against pro-admin-override,
     *  so passing true is a request rather than an authorization. */
    override?: boolean,
  ): Promise<AccessorialSaveResult> =>
    api
      .put(`/orders/${pronumber}/accessorials/${recno}`,
           override ? { ...body, override: true } : body)
      .then((r) => r.data),
  /** Soft-delete an accessorial with a mandatory reason (V1's Delete Accessorial).
   *  Separate from `update` because it also writes the reason to history, drops a
   *  note on the pro and emails the CSR. 409s once the accessorial has been
   *  invoiced, and unlike `update` there is no Admin Override arm — V1 never
   *  allows deleting a billed accessorial. */
  remove: (
    pronumber: number | string,
    recno: number,
    reason: string,
  ): Promise<AccessorialSaveResult> =>
    api
      .post(`/orders/${pronumber}/accessorials/${recno}/delete`, { reason })
      .then((r) => r.data),
};
